<template>
  <div class="runner-detail">
    <div class="back-btn" @click="goBack">← 返回列表</div>
    
    <div class="detail-header">
      <h2>跑手详情</h2>
    </div>

    <div class="detail-section">
      <h3>基本信息</h3>
      <div class="info-grid">
        <div class="info-item">
          <label>跑手ID</label>
          <span>{{ runnerDetail.id }}</span>
        </div>
        <div class="info-item">
          <label>用户ID</label>
          <span>{{ runnerDetail.userId }}</span>
        </div>
        <div class="info-item">
          <label>姓名</label>
          <span>{{ runnerDetail.real_name }}</span>
        </div>
        <div class="info-item">
          <label>手机号</label>
          <span>{{ runnerDetail.phone }}</span>
        </div>
        <div class="info-item">
          <div class="info-item-head">
            <label>跑手类型</label>
          </div>
          <div class="runner-type-line">
            <span>{{ runnerDetail.runnerTypeName || '未设置' }}</span>
            <button type="button" class="btn-link runner-type-edit-btn" @click="openTypeModal">编辑</button>
          </div>
        </div>
        <div class="info-item">
          <div class="info-item-head">
            <label>负责小区</label>
          </div>
          <div class="community-line">
            <span>{{ runnerDetail.community || '未设置' }}</span>
            <button type="button" class="btn-link add-community-inline-btn" @click="openAddCommunityModal">添加小区</button>
          </div>
        </div>
        <div class="info-item">
          <label>评分</label>
          <span>⭐{{ runnerDetail.rating }}</span>
        </div>
        <div class="info-item">
          <label>累计完成订单</label>
          <span>{{ runnerDetail.completedOrders }}单</span>
        </div>
        <div class="info-item">
          <label>跑手钱包余额</label>
          <span>{{ (runnerDetail.balance || 0) / 100 }}</span>
        </div>
        <div class="info-item">
          <label>今日收入</label>
          <span>{{ (runnerDetail.todayIncome || 0) / 100 }}</span>
        </div>
        <div class="info-item">
          <label>累计服务费</label>
          <span>{{ (runnerDetail.totalCommission || 0) / 100 }}</span>
        </div>
        <div class="info-item">
          <label>累计提现</label>
          <span>{{ (runnerDetail.totalWithdraw || 0) / 100 }}</span>
        </div>
        <div class="info-item">
          <label>累计支出</label>
          <span>{{ (runnerDetail.totalConsume || 0) / 100 }}</span>
        </div>
        <div class="info-item">
          <label>累计退款</label>
          <span>{{ (runnerDetail.totalRefund || 0) / 100 }}</span>
        </div>
        <div class="info-item">
          <label>在线状态</label>
          <span :class="['online-status', runnerDetail.onlineStatus === 1 ? 'online' : 'offline']">{{ runnerDetail.onlineStatus === 1 ? '在线' : '离线' }}</span>
        </div>
        <div class="info-item">
          <label>工作状态</label>
          <span :class="['work-status', runnerDetail.workStatus === 1 ? 'working' : 'idle']">{{ runnerDetail.workStatus === 1 ? '送单中' : '空闲' }}</span>
        </div>
        <div class="info-item">
          <div class="info-item-head">
            <label>最多同时接单数量</label>
          </div>
          <div class="max-order-line">
            <span>{{ runnerDetail.maxOrderCount != null ? runnerDetail.maxOrderCount : '-' }}</span>
            <button type="button" class="btn-link max-order-edit-btn" @click="openMaxOrderModal">编辑</button>
          </div>
        </div>
        <div class="info-item">
          <label>当前进行中订单数</label>
          <span>{{ runnerDetail.runningOrderCount != null ? runnerDetail.runningOrderCount : '-' }}</span>
        </div>
        <div class="info-item">
          <label>认证状态</label>
          <div class="status-row">
            <span :class="'cert-status cert-status-' + runnerDetail.certStatus">{{ getCertStatusName(runnerDetail.certStatus) }}</span>
            <button @click="approve" class="btn-success" v-if="runnerDetail.certStatus < 6">←已完成此步骤，下一步</button>
            <button @click="reject" class="btn-danger" v-if="runnerDetail.certStatus < 6">拒绝申请</button>
            <button @click="openCertModal" class="btn-primary">修改认证状态</button>
          </div>
        </div>
        <div class="info-item" v-if="runnerDetail.certRemark">
          <label>拒绝理由</label>
          <span>{{ runnerDetail.certRemark }}</span>
        </div>
        <div class="info-item">
          <label>账号状态</label>
          <div class="status-row">
            <span :class="['status', runnerDetail.status === 0 ? 'enabled' : 'disabled']">{{ runnerDetail.status === 0 ? '正常' : '禁用' }}</span>
            <button type="button" class="btn-link status-toggle-btn" @click="toggleRunnerStatus">{{ runnerDetail.status === 1 ? '启用' : '禁用' }}</button>
          </div>
        </div>
        <div class="info-item">
          <label>创建时间</label>
          <span>{{ runnerDetail.createdAt || '-' }}</span>
        </div>
        <div class="info-item">
          <div class="info-item-head">
            <label>跑手推广人</label>
          </div>
          <div class="runner-referrer-line">
            <span>{{ runnerDetail.runnerReferrerRealName || '未设置' }}</span>
            <button type="button" class="btn-link runner-referrer-edit-btn" @click="openReferrerModal">编辑</button>
          </div>
        </div>
      </div>
    </div>

    <div class="detail-section" v-if="runnerDetail.communities && runnerDetail.communities.length > 0">
      <div class="section-header">
        <h3>服务小区列表</h3>
        <button type="button" class="btn-link add-community-btn" @click="openAddCommunityModal">添加</button>
      </div>
      <div class="community-list">
        <div v-for="comm in runnerDetail.communities" :key="comm.id" class="community-item">
          <span class="community-info">
            <span class="community-name">
              {{ comm.name }}
              - 服务日期：
              {{ (comm.startTime || '').slice(0, 10) }} ~
              {{ (comm.endTime || '').slice(0, 10) || '永久有效' }}
            </span>
            <span v-if="comm.isDefault" class="default-tag">默认</span>
          </span>
          <button type="button" class="btn-link community-edit-btn" @click="openEditCommunityModal(comm)">编辑</button>
          <button type="button" class="btn-link community-delete-btn" @click="deleteCommunity(comm)">删除</button>
        </div>
      </div>
    </div>

    <div class="detail-section" v-if="runnerDetail.certStatus >= 0 && runnerDetail.certStatus <= 5">
      <h3>认证审核</h3>
      <div class="action-buttons">
        <button @click="approve" class="btn-success" v-if="runnerDetail.certStatus < 6">←已完成此步骤，下一步</button>
        <button @click="reject" class="btn-danger" v-if="runnerDetail.certStatus < 6">拒绝申请</button>
        <button @click="openCertModal" class="btn-primary">修改认证状态</button>
      </div>
    </div>

    <div class="detail-section" v-if="runnerDetail.certStatus === 6">
      <h3>操作</h3>
      <div class="action-buttons">
        <button @click="toggleRunnerStatus" :class="runnerDetail.status === 1 ? 'btn-success' : 'btn-danger'">
          {{ runnerDetail.status === 1 ? '启用' : '禁用' }}
        </button>
        <button @click="openCertModal" class="btn-primary">修改认证状态</button>
      </div>
    </div>

    <div class="detail-section" v-if="runnerDetail.certStatus === 7">
      <h3>操作</h3>
      <div class="action-buttons">
        <button @click="toggleRunnerStatus" :class="runnerDetail.status === 1 ? 'btn-success' : 'btn-danger'">
          {{ runnerDetail.status === 1 ? '启用' : '禁用' }}
        </button>
        <button @click="openCertModal" class="btn-primary">修改认证状态</button>
      </div>
    </div>

    <div class="detail-section" v-if="runnerDetail.certStatus === 6 || runnerDetail.certStatus === 7">
      <div class="section-header">
        <h3>当前进行中订单列表</h3>
      </div>
      <runner-order-list v-if="runnerDetail.id" :runner-id="runnerDetail.id" />
    </div>

    <div class="detail-section">
      <div class="section-header">
        <h3>跑手钱包流水</h3>
        <router-link v-if="runnerDetail.id" :to="`/finance/transactions?runner_id=${runnerDetail.id}`" class="btn-link">查看全部角色钱包</router-link>
      </div>
      <transaction-list :runner-id="runnerDetail.id" />
    </div>

    <div v-if="certModalVisible" class="modal-overlay" @click="closeCertModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>修改认证状态</h3>
          <span class="close-btn" @click="closeCertModal">×</span>
        </div>
        <div class="modal-body">
          <div class="form-item">
            <label>认证状态</label>
            <select v-model="certForm.certStatus" class="form-select">
              <option v-for="opt in certStatusOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
            </select>
          </div>
          <div class="form-item" v-if="certForm.certStatus === 7">
            <label>拒绝理由</label>
            <textarea v-model="certForm.certRemark" class="form-textarea" placeholder="请输入拒绝理由"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeCertModal" class="btn-cancel">取消</button>
          <button @click="submitCertStatus" class="btn-submit">确认修改</button>
        </div>
      </div>
    </div>

    <div v-if="addCommunityModalVisible" class="modal-overlay" @click="closeAddCommunityModal">
      <div class="modal-content community-modal" @click.stop>
        <div class="modal-header">
          <h3>添加此跑手负责的小区</h3>
          <span class="close-btn" @click="closeAddCommunityModal">×</span>
        </div>
        <div class="modal-body">
          <div class="form-item">
            <label>所在地区</label>
            <div class="region-select">
              <select v-model="addProvinceId" @change="handleAddProvinceChange" class="region-select-item">
                <option value="">请选择省</option>
                <option v-for="p in addProvinceList" :key="p.id" :value="p.id">{{ p.name }}</option>
              </select>
              <select v-model="addCityId" @change="handleAddCityChange" class="region-select-item" :disabled="!addProvinceId">
                <option value="">请选择市</option>
                <option v-for="c in addCityList" :key="c.id" :value="c.id">{{ c.name }}</option>
              </select>
              <select v-model="addDistrictId" @change="handleAddDistrictChange" class="region-select-item" :disabled="!addCityId">
                <option value="">请选择区县</option>
                <option v-for="d in addDistrictList" :key="d.id" :value="d.id">{{ d.name }}</option>
              </select>
            </div>
          </div>
          <div class="form-item">
            <input v-model="addCommunitySearch" class="form-input" placeholder="搜索小区名称" />
          </div>
          <div class="community-check-list">
            <label v-for="comm in filteredAddCommunities" :key="comm.id" class="community-check-item">
              <input type="radio" :value="comm.id" v-model="addSelectedCommunityId" />
              <span>{{ comm.name }}</span>
            </label>
            <div v-if="filteredAddCommunities.length === 0" class="empty-tip">暂无小区</div>
          </div>
          <div class="form-item">
            <label>开始服务时间</label>
            <input type="date" v-model="addCommunityStartTime" class="form-input" />
          </div>
          <div class="form-item">
            <label>结束服务时间（不填表示永久有效）</label>
            <input type="date" v-model="addCommunityEndTime" class="form-input" />
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeAddCommunityModal" class="btn-cancel">取消</button>
          <button @click="saveAddCommunity" class="btn-submit" :disabled="!addSelectedCommunityId">确认添加</button>
        </div>
      </div>
    </div>
    <div v-if="editCommunityModalVisible" class="modal-overlay" @click="closeEditCommunityModal">
      <div class="modal-content community-modal" @click.stop>
        <div class="modal-header">
          <h3>编辑负责的小区</h3>
          <span class="close-btn" @click="closeEditCommunityModal">×</span>
        </div>
        <div class="modal-body">
          <div class="form-item">
            <label>小区名称</label>
            <span class="readonly-text">{{ editCommunity.name }}</span>
          </div>
          <div class="form-item">
            <label>开始服务时间</label>
            <input type="date" v-model="editCommunityStartTime" class="form-input" />
          </div>
          <div class="form-item">
            <label>结束服务时间（不填表示永久有效）</label>
            <input type="date" v-model="editCommunityEndTime" class="form-input" />
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeEditCommunityModal" class="btn-cancel">取消</button>
          <button @click="saveEditCommunity" class="btn-submit">确认修改</button>
        </div>
      </div>
    </div>

    <div v-if="typeModalVisible" class="modal-overlay" @click="closeTypeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>修改跑手类型</h3>
          <span class="close-btn" @click="closeTypeModal">×</span>
        </div>
        <div class="modal-body">
          <div class="form-item">
            <label>当前类型</label>
            <span class="readonly-text">{{ runnerDetail.runnerTypeName || '未设置' }}</span>
          </div>
          <div class="form-item">
            <label>修改为</label>
            <select v-model="selectedTypeId" class="form-select">
              <option v-for="opt in typeOptions" :key="opt.id" :value="opt.id">{{ opt.name }}</option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeTypeModal" class="btn-cancel">取消</button>
          <button @click="submitType" class="btn-submit" :disabled="!selectedTypeId">确认修改</button>
        </div>
      </div>
    </div>

    <div v-if="referrerModalVisible" class="modal-overlay" @click="closeReferrerModal">
      <div class="modal-content community-modal" @click.stop>
        <div class="modal-header">
          <h3>修改跑手推广人</h3>
          <span class="close-btn" @click="closeReferrerModal">×</span>
        </div>
        <div class="modal-body">
          <div class="form-item">
            <label>当前跑手推广人</label>
            <span class="readonly-text">{{ runnerDetail.runnerReferrerRealName || '未设置' }}</span>
          </div>
          <div class="form-item">
            <input v-model="referrerSearch" class="form-input" placeholder="搜索跑手推广人姓名/手机号" />
          </div>
          <div class="community-check-list">
            <label class="community-check-item">
              <input type="radio" :value="null" v-model="selectedReferrerId" />
              <span>不关联跑手推广人</span>
            </label>
            <label v-for="r in filteredReferrers" :key="r.id" class="community-check-item">
              <input type="radio" :value="r.id" v-model="selectedReferrerId" />
              <span>{{ referrerName(r) }}（{{ r.phone || '-' }}）</span>
            </label>
            <div v-if="filteredReferrers.length === 0" class="empty-tip">暂无跑手推广人</div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeReferrerModal" class="btn-cancel">取消</button>
          <button @click="submitReferrer" class="btn-submit">确认修改</button>
        </div>
      </div>
    </div>
    <div v-if="maxOrderModalVisible" class="modal-overlay" @click="closeMaxOrderModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>修改最多同时接单数量</h3>
          <span class="close-btn" @click="closeMaxOrderModal">×</span>
        </div>
        <div class="modal-body">
          <div class="form-item">
            <label>当前数量</label>
            <span class="readonly-text">{{ runnerDetail.maxOrderCount != null ? runnerDetail.maxOrderCount : '-' }}</span>
          </div>
          <div class="form-item">
            <label>修改为（1-10 的整数）</label>
            <input type="number" min="1" max="10" v-model.number="maxOrderForm.maxOrderCount" class="form-input" />
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeMaxOrderModal" class="btn-cancel">取消</button>
          <button @click="submitMaxOrder" class="btn-submit">确认修改</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getRunnerDetail, approveRunner, rejectRunner, updateRunnerStatus, updateRunnerCertStatus, updateRunnerType, updateRunnerReferrer, updateRunnerMaxOrderCount, addRunnerCommunity, updateRunnerCommunity, deleteRunnerCommunity } from '@/api/runners'
import { getCommunityList } from '@/api/communities'
import { getRunnerTypeOptions } from '@/api/runnerTypes'
import { getRunnerReferrerOptions } from '@/api/runners'
import { getProvinces, getCities, getDistricts } from '@/api/regions'
import TransactionList from '@/components/TransactionList.vue'
import RunnerOrderList from '@/components/RunnerOrderList.vue'

export default {
  name: 'RunnerDetail',
  components: {
    TransactionList,
    RunnerOrderList
  },
  data() {
    return {
      runnerDetail: {},
      certModalVisible: false,
      certForm: {
        certStatus: 0,
        certRemark: ''
      },
      addCommunityModalVisible: false,
      addAllCommunities: [],
      addSelectedCommunityId: null,
      addCommunitySearch: '',
      addCommunityStartTime: '',
      addCommunityEndTime: '',
      addProvinceId: '',
      addCityId: '',
      addDistrictId: '',
      addProvinceList: [],
      addCityList: [],
      addDistrictList: [],
      editCommunityModalVisible: false,
      editCommunity: {},
      editCommunityStartTime: '',
      editCommunityEndTime: '',
      typeModalVisible: false,
      typeOptions: [],
      selectedTypeId: null,
      referrerModalVisible: false,
      referrerList: [],
      referrerSearch: '',
      selectedReferrerId: null,
      maxOrderModalVisible: false,
      maxOrderForm: {
        maxOrderCount: 1
      },
      certStatusOptions: [
        { value: 0, label: '待邀约' },
        { value: 1, label: '待面谈' },
        { value: 2, label: '待核验原件' },
        { value: 3, label: '待提交照片' },
        { value: 4, label: '待核验照片' },
        { value: 5, label: '待签约' },
        { value: 6, label: '已签约' },
        { value: 7, label: '已拒绝' }
      ]
    }
  },
  mounted() {
    const id = this.$route.params.id
    if (id) {
      this.fetchDetail(id)
    }
  },
  computed: {
    filteredAddCommunities() {
      let list = this.addAllCommunities
      if (this.addProvinceId) {
        list = list.filter(c => String(c.province_id ?? c.provinceId ?? '') === String(this.addProvinceId))
      }
      if (this.addCityId) {
        list = list.filter(c => String(c.city_id ?? c.cityId ?? '') === String(this.addCityId))
      }
      if (this.addDistrictId) {
        list = list.filter(c => String(c.district_id ?? c.districtId ?? '') === String(this.addDistrictId))
      }
      const keyword = this.addCommunitySearch.trim().toLowerCase()
      if (keyword) {
        list = list.filter(c => (c.name || '').toLowerCase().includes(keyword))
      }
      return list
    },
    filteredReferrers() {
      const kw = this.referrerSearch.trim().toLowerCase()
      if (!kw) {
        return this.referrerList
      }
      return this.referrerList.filter(r => {
        const name = this.referrerName(r).toLowerCase()
        const phone = (r.phone ? String(r.phone) : '').toLowerCase()
        return name.includes(kw) || phone.includes(kw)
      })
    }
  },
  methods: {
    getCertStatusName(status) {
      const map = { 0: '待邀约', 1: '待面谈', 2: '待核验原件', 3: '待提交照片', 4: '待核验照片', 5: '待签约', 6: '已签约', 7: '已拒绝' }
      return map[status] || '未知'
    },
    async fetchDetail(id) {
      try {
        const res = await getRunnerDetail(id)
        if (res.code === 0) {
          this.runnerDetail = res.data || {}
        }
      } catch (error) {
      }
    },
    goBack() {
      this.$router.push('/user/runners')
    },
    async approve() {
      if (confirm('确定已完成此步骤，进入下一步吗？')) {
        try {
          const res = await approveRunner(this.runnerDetail.id)
          if (res.code === 0) {
            alert(res.message || '已进入下一步')
            this.fetchDetail(this.runnerDetail.id)
          } else {
            alert(res.message || '操作失败')
          }
        } catch (error) {
          alert('操作失败')
        }
      }
    },
    async reject() {
      const remark = prompt('请输入拒绝理由：')
      if (remark) {
        try {
          const res = await rejectRunner(this.runnerDetail.id, remark)
          if (res.code === 0) {
            alert('拒绝认证成功')
            this.fetchDetail(this.runnerDetail.id)
          } else {
            alert(res.message || '操作失败')
          }
        } catch (error) {
          alert('操作失败')
        }
      }
    },

    async toggleRunnerStatus() {
      const newStatus = this.runnerDetail.status === 0 ? 1 : 0
      const actionText = newStatus === 1 ? '禁用' : '启用'
      if (confirm(`确定要${actionText}跑手 ${this.runnerDetail.real_name} 吗？`)) {
        try {
          const res = await updateRunnerStatus(this.runnerDetail.id, newStatus)
          if (res.code === 0) {
            alert(`${actionText}成功`)
            this.fetchDetail(this.runnerDetail.id)
          } else {
            alert(res.message || '操作失败')
          }
        } catch (error) {
          alert('操作失败')
        }
      }
    },
    openCertModal() {
      this.certForm.certStatus = this.runnerDetail.certStatus || 0
      this.certForm.certRemark = this.runnerDetail.certRemark || ''
      this.certModalVisible = true
    },
    async openAddCommunityModal() {
      try {
        const res = await getCommunityList({ page: 1, pageSize: 999 })
        this.addAllCommunities = (res.code === 0 && res.data) ? (res.data.list || []) : []
      } catch (error) {
        this.addAllCommunities = []
      }
      this.addSelectedCommunityId = null
      this.addCommunitySearch = ''
      const today = new Date()
      const pad = n => String(n).padStart(2, '0')
      this.addCommunityStartTime = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`
      this.addCommunityEndTime = ''
      this.addProvinceId = ''
      this.addCityId = ''
      this.addDistrictId = ''
      this.addCityList = []
      this.addDistrictList = []
      await this.loadAddProvinces()
      this.addCommunityModalVisible = true
    },
    async loadAddProvinces() {
      try {
        const res = await getProvinces()
        this.addProvinceList = res.code === 200 ? (res.data || []) : []
      } catch (error) {
        this.addProvinceList = []
      }
    },
    async handleAddProvinceChange() {
      this.addCityId = ''
      this.addDistrictId = ''
      this.addCityList = []
      this.addDistrictList = []
      this.addSelectedCommunityId = null
      if (this.addProvinceId) {
        try {
          const res = await getCities(this.addProvinceId)
          this.addCityList = res.code === 200 ? (res.data || []) : []
        } catch (error) {
          this.addCityList = []
        }
      }
    },
    async handleAddCityChange() {
      this.addDistrictId = ''
      this.addDistrictList = []
      this.addSelectedCommunityId = null
      if (this.addCityId) {
        try {
          const res = await getDistricts(this.addCityId)
          this.addDistrictList = res.code === 200 ? (res.data || []) : []
        } catch (error) {
          this.addDistrictList = []
        }
      }
    },
    handleAddDistrictChange() {
      this.addSelectedCommunityId = null
    },
    closeAddCommunityModal() {
      this.addCommunityModalVisible = false
    },
    openEditCommunityModal(comm) {
      this.editCommunity = comm
      this.editCommunityStartTime = (comm.startTime || '').slice(0, 10)
      this.editCommunityEndTime = (comm.endTime || '').slice(0, 10)
      this.editCommunityModalVisible = true
    },
    closeEditCommunityModal() {
      this.editCommunityModalVisible = false
    },
    async saveEditCommunity() {
      if (!this.editCommunity || !this.editCommunity.id) {
        alert('请选择要编辑的小区')
        return
      }
      if (confirm('确定要修改该负责小区的信息吗？')) {
        try {
          const res = await updateRunnerCommunity(
            this.runnerDetail.id,
            this.editCommunity.id,
            this.editCommunityStartTime,
            this.editCommunityEndTime
          )
          if (res.code === 0) {
            alert('小区信息更新成功')
            this.closeEditCommunityModal()
            this.fetchDetail(this.runnerDetail.id)
          } else {
            alert(res.message || '操作失败')
          }
        } catch (error) {
          alert('操作失败')
        }
      }
    },
    async deleteCommunity(comm) {
      if (!comm || !comm.id) {
        alert('请选择要删除的小区')
        return
      }
      if (confirm(`确定要移除小区「${comm.name}」吗？此操作仅软删除该负责关系。`)) {
        try {
          const res = await deleteRunnerCommunity(this.runnerDetail.id, comm.id)
          if (res.code === 0) {
            alert('小区移除成功')
            this.fetchDetail(this.runnerDetail.id)
          } else {
            alert(res.message || '操作失败')
          }
        } catch (error) {
          alert('操作失败')
        }
      }
    },
    async saveAddCommunity() {
      if (!this.addSelectedCommunityId) {
        alert('请选择要添加的小区')
        return
      }
      if (confirm('确定要添加该负责小区吗？')) {
        try {
          const res = await addRunnerCommunity(this.runnerDetail.id, this.addSelectedCommunityId, this.addCommunityStartTime, this.addCommunityEndTime)
          if (res.code === 0) {
            alert('小区添加成功')
            this.closeAddCommunityModal()
            this.fetchDetail(this.runnerDetail.id)
          } else {
            alert(res.message || '操作失败')
          }
        } catch (error) {
          alert('操作失败')
        }
      }
    },
    closeCertModal() {
      this.certModalVisible = false
    },
    async submitCertStatus() {
      if (this.certForm.certStatus === 7 && !this.certForm.certRemark.trim()) {
        alert('请输入拒绝理由')
        return
      }
      if (confirm('确定要修改认证状态吗？')) {
        try {
          const res = await updateRunnerCertStatus(this.runnerDetail.id, this.certForm.certStatus, this.certForm.certRemark)
          if (res.code === 0) {
            alert('认证状态修改成功')
            this.closeCertModal()
            this.fetchDetail(this.runnerDetail.id)
          } else {
            alert(res.message || '操作失败')
          }
        } catch (error) {
          alert('操作失败')
        }
      }
    },
    async openTypeModal() {
      try {
        const res = await getRunnerTypeOptions()
        this.typeOptions = (res.code === 0 && res.data && res.data.runner_types) ? res.data.runner_types : []
        this.selectedTypeId = this.runnerDetail.runnerTypeId || null
        this.typeModalVisible = true
      } catch (error) {
        alert('加载跑手类型失败')
      }
    },
    closeTypeModal() {
      this.typeModalVisible = false
    },
    async submitType() {
      if (!this.selectedTypeId) {
        alert('请选择跑手类型')
        return
      }
      if (confirm('确定要修改该跑手的跑手类型吗？')) {
        try {
          const res = await updateRunnerType(this.runnerDetail.id, this.selectedTypeId)
          if (res.code === 0) {
            alert('跑手类型修改成功')
            this.closeTypeModal()
            this.fetchDetail(this.runnerDetail.id)
          } else {
            alert(res.message || '操作失败')
          }
        } catch (error) {
          alert('操作失败')
        }
      }
    },
    referrerName(r) {
      if (!r) return '未设置'
      return r.realName || '未设置'
    },
    async openReferrerModal() {
      try {
        const res = await getRunnerReferrerOptions({ keyword: '' })
        this.referrerList = (res.code === 0 && res.data) ? (res.data.list || []) : []
      } catch (error) {
        this.referrerList = []
        alert('加载跑手推广人列表失败')
      }
      this.selectedReferrerId = this.runnerDetail.runnerReferrerId ?? null
      this.referrerSearch = ''
      this.referrerModalVisible = true
    },
    closeReferrerModal() {
      this.referrerModalVisible = false
    },
    async submitReferrer() {
      if (confirm('确定要修改该跑手的推广人吗？')) {
        try {
          const res = await updateRunnerReferrer(this.runnerDetail.id, this.selectedReferrerId)
          if (res.code === 0) {
            alert('跑手推广人修改成功')
            this.closeReferrerModal()
            this.fetchDetail(this.runnerDetail.id)
          } else {
            alert(res.message || '操作失败')
          }
        } catch (error) {
          alert('操作失败')
        }
      }
    },
    openMaxOrderModal() {
      this.maxOrderForm.maxOrderCount = this.runnerDetail.maxOrderCount != null ? this.runnerDetail.maxOrderCount : 1
      this.maxOrderModalVisible = true
    },
    closeMaxOrderModal() {
      this.maxOrderModalVisible = false
    },
    async submitMaxOrder() {
      const val = Number(this.maxOrderForm.maxOrderCount)
      if (!Number.isInteger(val) || val < 1 || val > 10) {
        alert('请输入 1-10 之间的整数')
        return
      }
      if (confirm('确定要修改该跑手最多同时接单数量吗？')) {
        try {
          const res = await updateRunnerMaxOrderCount(this.runnerDetail.id, val)
          if (res.code === 0) {
            alert('最多同时接单数量修改成功')
            this.closeMaxOrderModal()
            this.fetchDetail(this.runnerDetail.id)
          } else {
            alert(res.message || '操作失败')
          }
        } catch (error) {
          alert('操作失败')
        }
      }
    }
  }
}
</script>

<style scoped>
.runner-detail {
  padding: 20px;
}

.back-btn {
  color: #3498db;
  cursor: pointer;
  margin-bottom: 20px;
  font-size: 14px;
}

.detail-header {
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 20px;
}

.detail-header h2 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.detail-section {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.detail-section h3 {
  margin: 0 0 15px 0;
  font-size: 16px;
  color: #333;
  padding-bottom: 10px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.info-item {
  display: flex;
  flex-direction: column;
}

.info-item label {
  font-size: 13px;
  color: #999;
  margin-bottom: 5px;
}

.info-item-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 5px;
}

.info-item-head label {
  margin-bottom: 0;
}

.community-line {
  display: flex;
  align-items: center;
  gap: 12px;
}

.runner-referrer-line {
  display: flex;
  align-items: center;
  gap: 12px;
}

.runner-type-line {
  display: flex;
  align-items: center;
  gap: 12px;
}

.runner-type-edit-btn {
  margin: 0;
  align-self: center;
  padding: 2px 10px;
  font-size: 12px;
  white-space: nowrap;
}

.max-order-line {
  display: flex;
  align-items: center;
  gap: 12px;
}

.max-order-edit-btn {
  margin: 0;
  align-self: center;
  padding: 2px 10px;
  font-size: 12px;
  white-space: nowrap;
}

.add-community-inline-btn {
  margin: 0;
  align-self: center;
  padding: 2px 10px;
  font-size: 12px;
}

.info-item span {
  font-size: 14px;
  color: #333;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.section-header h3 {
  margin: 0;
}

.add-community-btn {
  margin: 0;
}

.community-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.community-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 16px;
  background: #f5f7fa;
  border-radius: 8px;
}

.community-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.community-name {
  font-size: 14px;
  color: #333;
}

.community-edit-btn {
  margin-left: auto;
  margin-top: 0;
  padding: 2px 10px;
  font-size: 12px;
}

.community-delete-btn {
  margin-top: 0;
  padding: 2px 10px;
  font-size: 12px;
  color: #e74c3c;
  border-color: #e74c3c;
}

.community-delete-btn:hover {
  background: #e74c3c;
  color: #fff;
}

.readonly-text {
  font-size: 14px;
  color: #333;
}

.default-tag {
  font-size: 12px;
  color: #3498db;
  background: #e8f4fd;
  padding: 2px 8px;
  border-radius: 10px;
}

.cert-status {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
}

.cert-status-0 { background: #f5f5f5; color: #666; }
.cert-status-1 { background: #fff3e0; color: #f39c12; }
.cert-status-2 { background: #e8f5e9; color: #2ecc71; }
.cert-status-3 { background: #ffebee; color: #e74c3c; }

.online-status {
  align-self: flex-start;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
}

.online-status.online { background: #e8f5e9; color: #2ecc71; }
.online-status.offline { background: #ffebee; color: #e74c3c; }

.work-status {
  align-self: flex-start;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
}

.work-status.working { background: #e8f5e9; color: #2ecc71; }
.work-status.idle { background: #f5f5f5; color: #666; }

.status {
  align-self: flex-start;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
}

.status.enabled { background: #e8f5e9; color: #2ecc71; }
.status.disabled { background: #ffebee; color: #e74c3c; }

.action-buttons {
  display: flex;
  gap: 15px;
}

.btn-success {
  padding: 10px 24px;
  background: #2ecc71;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.btn-danger {
  padding: 10px 24px;
  background: #e74c3c;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.btn-warning {
  padding: 10px 24px;
  background: #f39c12;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.btn-primary {
  padding: 10px 24px;
  background: #3498db;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  width: 400px;
  max-width: 90%;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
  color: #333;
}

.close-btn {
  font-size: 24px;
  color: #999;
  cursor: pointer;
  line-height: 1;
}

.close-btn:hover {
  color: #333;
}

.modal-body {
  padding: 20px;
}

.form-item {
  margin-bottom: 15px;
}

.form-item label {
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.form-select {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
  min-height: 80px;
  resize: vertical;
}

.btn-link {
  align-self: flex-start;
  margin-top: 8px;
  padding: 4px 10px;
  background: transparent;
  color: #3498db;
  border: 1px solid #3498db;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  text-decoration: none;
}

.status-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-toggle-btn {
  margin: 0;
}

.btn-link:hover {
  background: #3498db;
  color: #fff;
}

.community-modal {
  width: 480px;
}

.region-select {
  display: flex;
  gap: 8px;
}

.region-select-item {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
}

.region-select-item:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.form-input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.community-check-list {
  max-height: 320px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
}

.community-check-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
}

.empty-tip {
  font-size: 13px;
  color: #999;
  text-align: center;
  padding: 20px 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 20px;
  border-top: 1px solid #eee;
}

.btn-cancel {
  padding: 10px 20px;
  background: #f5f5f5;
  color: #666;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.btn-submit {
  padding: 10px 20px;
  background: #3498db;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}
</style>
